/**
 * The registry of verified composite capabilities, and the single answer to
 * "may this composite plan be executed and priced?".
 *
 * Every other composite module asks this one module rather than carrying its
 * own list: the executor refuses a cast with it, the loader gates
 * `READY_COMPOSITE` on it, and appraisal consults it before pricing. That
 * matters because the combinations this feature has actually verified are a
 * small subset of the combinations the schema can express, and three modules
 * each maintaining their own subset is how a shape ends up executable but
 * unpriced, or priced but unexecutable.
 *
 * ## The registration seam
 *
 * A plan is decomposed into the set of capability IDs its shape *requires*,
 * and the plan is supported only when every required ID is open. Work that
 * adds a new composite shape therefore touches exactly one line of shared
 * state — flipping its own ID to `open: true` once its evidence exists — and
 * keeps its implementation and tests in its own modules. Two such efforts
 * can proceed in parallel without contending over a shared list of rules.
 *
 * The rule throughout is the one the canonical contract states: **schema
 * validity never produces `READY`.** A capability opens only where
 * valuation, runtime execution and placement evidence all exist, and a
 * combination of individually-open capabilities is not automatically open —
 * which is why this reasons about the assembled plan rather than checking
 * each axis in isolation.
 */
import { castReachLimits, castUnitCap } from '../burst/profile.js';
import { castBudgetTruncation, CAST_BUDGET_TRUNCATION } from './composite-cast-budget.js';
import { STAGE_EVENT_COUNT_MODEL_UNAVAILABLE, uncountableStageEvent } from './composite-event-bound.js';
import { resolveLegacyDeliveryForApplication } from './application.js';
import type { CompiledSkillPlanV3, CompiledStageV3 } from './composite-compiler.js';
import {
  isDegenerateCompositeSkill,
  type AttackStageV1,
  type CanonicalSkillV3Input,
  type MemberScheduleV1,
  type SpatialFormationV1,
  type StageEventAnchorV1,
  type StageTimingV1,
  type VolleyScheduleV1,
} from './composite-types.js';
import type { CanonicalMotionV2 } from './types.js';
import type { StageMotionV1 } from './composite-types.js';
import { deliveryPlacementMode, isIdentityTransform } from '../sim/attack-pose.js';
import { scheduleCompositeCast } from '../sim/composite-stage-scheduler.js';
import { statusStageDepths } from './skill-status-rules.js';
import { canonicalStatusModel } from './composite-validate.js';
import { SVP_0_1, type BalanceProfile } from '../profile.js';
import { legalityContextKey, unlimitedHostContext, type LegalityContext } from './legality-context.js';

/**
 * Capability identities, following the spec's §6 matrix where a row maps
 * cleanly onto something the engine can decide from a compiled plan.
 *
 * Deliberately narrower than the spec's names in one place: the spec's
 * `PROJECTILE_VOLLEY` covers FAN, PARALLEL and RADIAL together, but only FAN
 * has world placement, so it is split rather than opened wholesale. Granting
 * a whole row on the strength of one verified member is the move §6 forbids.
 */
export type CompositeCapabilityId =
  | 'SINGLE_V3_COMPAT'
  | 'PARALLEL_STAGES'
  | 'REPEATED_VOLLEY'
  | 'FAN_VOLLEY'
  | 'PARALLEL_VOLLEY'
  | 'RADIAL_VOLLEY'
  | 'RING_PLACEMENT'
  | 'SCATTER_PLACEMENT'
  | 'WEAPON_SWEEP_STAGE'
  | 'STAGE_FRACTION_TIMING'
  | 'MULTI_FOOTPRINT_UNION'
  | 'WEAPON_FOOTPRINT_UNION'
  | 'DELAYED_STAGE_TIMING'
  | 'STAGE_EVENT_TIMING'
  | 'STAGE_EVENT_SCOPED_EVERY_N'
  | 'STAGE_MARK_DETONATION'
  | 'SKILL_STATUS'
  | 'ELEMENT_STATUS'
  | 'CHAIN_FORMATION'
  | 'SEQUENTIAL_MEMBERS'
  | 'STAGGERED_MEMBERS'
  | 'PROJECTILE_STAGE'
  | 'AREA_POINT_PLACEMENT'
  | 'ORBIT_MOTION'
  | 'ORBIT_SWEPT_CONTACT'
  | 'ORBIT_REHIT'
  | 'ORBIT_EVALUATION'
  | 'ORBIT_LIFECYCLE'
  | 'DASH_ATTACK'
  | 'NON_AIMED_DELIVERY_PLACEMENT';

interface CapabilityEntry {
  /** Open only when valuation, execution and placement evidence all exist. */
  readonly open: boolean;
  /** Reported when a plan requires a closed capability. Stable: callers and
   * tests match on it, and all four boundaries report the same one. */
  readonly deferralCode: string;
  readonly summary: string;
}

/**
 * The registry. One line per capability; flipping `open` is the entire
 * shared surface a new composite feature needs to touch.
 */
export const COMPOSITE_CAPABILITIES: Readonly<Record<CompositeCapabilityId, CapabilityEntry>> = Object.freeze({
  SINGLE_V3_COMPAT: {
    open: true,
    deferralCode: 'COMPOSITE_SINGLE_COMPAT_UNAVAILABLE',
    summary: 'a migrated single attack, behaviourally identical to its V2 original',
  },
  PARALLEL_STAGES: {
    open: true,
    deferralCode: 'COMPOSITE_PARALLEL_STAGES_UNAVAILABLE',
    summary: 'several sibling attack stages resolving in one cast',
  },
  REPEATED_VOLLEY: {
    open: true,
    deferralCode: 'COMPOSITE_REPEATED_VOLLEY_UNAVAILABLE',
    summary: 'a stage repeating on a fixed interval, each volley its own attack',
  },
  FAN_VOLLEY: {
    open: true,
    deferralCode: 'COMPOSITE_FORMATION_PLACEMENT_UNIMPLEMENTED',
    summary: 'a FAN of independently-resolved members on an aim-governed delivery',
  },
  PARALLEL_VOLLEY: {
    open: true,
    deferralCode: 'COMPOSITE_FORMATION_PLACEMENT_UNIMPLEMENTED',
    summary: 'laterally offset parallel members, each released from its own point',
  },
  RADIAL_VOLLEY: {
    open: true,
    deferralCode: 'COMPOSITE_FORMATION_PLACEMENT_UNIMPLEMENTED',
    summary: 'members distributed over the full circle',
  },
  RING_PLACEMENT: {
    open: true,
    deferralCode: 'COMPOSITE_FORMATION_PLACEMENT_UNIMPLEMENTED',
    summary: 'members standing on a ring around the anchor, each facing outward',
  },
  SCATTER_PLACEMENT: {
    open: true,
    deferralCode: 'COMPOSITE_FORMATION_PLACEMENT_UNIMPLEMENTED',
    summary: 'deterministically scattered members within a radius',
  },
  WEAPON_SWEEP_STAGE: {
    open: true,
    // A sweep is not a moment, it is an interval driven by a host-owned
    // cursor. Whether one behaves correctly as a sibling stage — cost,
    // frozen source, cancellation, root lifetime — is its own question.
    deferralCode: 'COMPOSITE_WEAPON_SWEEP_STAGE_UNVERIFIED',
    summary: 'a timed weapon sweep as one stage of a composite cast (P5)',
  },
  STAGE_FRACTION_TIMING: {
    open: true,
    // Releasing partway through a sibling's own clock. The arithmetic
    // exists; what is unproven is that the release really tracks that
    // sibling's progress and really does not depend on it connecting.
    deferralCode: 'COMPOSITE_STAGE_FRACTION_TIMING_UNVERIFIED',
    summary: 'a stage released at a fraction of an earlier stage\'s own clock (P5)',
  },
  DELAYED_STAGE_TIMING: {
    open: true,
    deferralCode: 'COMPOSITE_DELAYED_STAGE_TIMING_UNVERIFIED',
    summary: 'a stage released a fixed number of ticks after the cast',
  },
  STAGE_EVENT_TIMING: {
    open: true,
    // A stage released by an event is priced as its own hits times how often
    // that event happens (`eval/stage-event-hits.ts`), which numeric allocation,
    // the skill summary and the budget walk all read; the runtime releases it
    // from every motion's hit, kill and end (the stage-event matrix in
    // `test/uniform-effect-rule-matrix.test.ts`). The burst probe cannot fire a
    // stage released by a kill (its target does not die) and refuses such a plan
    // by its own code (`COMPOSITE_BURST_KILL_EVENT_UNMEASURABLE`).
    deferralCode: 'COMPOSITE_STAGE_EVENT_TIMING_UNVERIFIED',
    summary: 'a full stage released when an earlier stage hits, kills, ends or has hit n times',
  },
  STAGE_EVENT_SCOPED_EVERY_N: {
    // Closed on valuation: the runtime keeps a counter per unit or per enemy, but
    // no price model counts how often one of them alone reaches n
    // (`uncountableStageEvent`, `composite-event-bound.ts`).
    open: false,
    deferralCode: STAGE_EVENT_COUNT_MODEL_UNAVAILABLE,
    summary: 'a stage released at every n-th hit of one unit or one enemy',
  },
  STAGE_MARK_DETONATION: {
    // Detonation releases once per marked enemy and scales its damage by that enemy's
    // cast-scoped stacks. Allocation, appraisal and every composite runtime use the
    // source's contacts on targets that never die; the timing kind alone cannot
    // represent this anchor's release count and stack weight.
    open: true,
    deferralCode: 'COMPOSITE_STAGE_MARK_DETONATION_UNVERIFIED',
    summary: 'a stage released once at every enemy carrying the cast\'s mark, as strong as that enemy\'s stacks',
  },
  SKILL_STATUS: {
    // Open because valuation, execution and placement evidence exist: the runtime creates, binds, hooks, sweeps and
    // clears a skill's own statuses in every composite host; the price counts everything a status makes possible from
    // one application timeline (`eval/status-timeline.ts`), which numeric allocation, final legality and a stored
    // skill's measure all read, and refuses by name what it does not model; the burst probe fires the hooks and
    // stands its target where they land. Like `STAGE_MARK_DETONATION`, no gate that reads only the timing kind sees
    // every use of one: a status leaf, a binding and a sweep anchor are each classified below.
    open: true,
    deferralCode: 'COMPOSITE_SKILL_STATUS_UNVERIFIED',
    summary: 'a status the skill names for itself: created by a stage, bound to by effects, hooked by stages, swept at its carriers',
  },
  ELEMENT_STATUS: {
    // The engine (parents, bound children, provenance, reactions) exists and is tested on a test-only package, and the price
    // counts what a status and its reactions can do; what is not proven is any shipped package. A package opens by adding its
    // name to `ELEMENT_STATUS_OPEN_NAMES` when its own tests pass, so this capability stays closed until a name is left that is not open.
    open: false,
    deferralCode: 'COMPOSITE_ELEMENT_STATUS_UNVERIFIED',
    summary: 'an element status (a neutral parent with bound effects) and the reactions it enables',
  },
  CHAIN_FORMATION: {
    // The runtime (both hosts), the price (`eval/chain-hits.ts`) and the burst probe's chain scenario agree, and the Lab
    // (`test/monte-carlo/chain-hits-lab.test.ts`) holds the realized hits to the priced ones within its band.
    open: true,
    deferralCode: 'COMPOSITE_CHAIN_FORMATION_UNVERIFIED',
    summary: 'a stage whose members go from enemy to enemy: picked when a member strikes (ricochet) or planned at the first strike (chain)',
  },
  SEQUENTIAL_MEMBERS: {
    open: true,
    deferralCode: 'COMPOSITE_SEQUENTIAL_MEMBERS_UNVERIFIED',
    summary: 'members of one volley released one after another, each on its own tick',
  },
  STAGGERED_MEMBERS: {
    open: false,
    deferralCode: 'COMPOSITE_STAGGERED_MEMBERS_UNVERIFIED',
    summary: 'members of one volley scattered across a release window (P7)',
  },
  PROJECTILE_STAGE: {
    open: true,
    deferralCode: 'COMPOSITE_PROJECTILE_STAGE_UNVERIFIED',
    summary: 'a stage whose attack travels as a projectile',
  },
  MULTI_FOOTPRINT_UNION: {
    open: true,
    deferralCode: 'COMPOSITE_UNION_FOOTPRINT_PLACEMENT_UNIMPLEMENTED',
    summary: 'one skill-owned attack covering several footprints, deduplicated per target',
  },
  WEAPON_FOOTPRINT_UNION: {
    open: true,
    deferralCode: 'COMPOSITE_WEAPON_UNION_REGION_POLICY_UNDECIDED',
    summary: 'one equipped-weapon attack over several transformed contact regions; the largest region fraction applies',
  },
  AREA_POINT_PLACEMENT: {
    open: true,
    // A ground circle is anchored at a resolved POINT, so it is placed by
    // handing the runtime the point the formation resolved. Handing it a
    // direction instead would project the circle forward by its own
    // radius — a translation the authored transform never asked for, and
    // the reason areas were withheld until placement had two modes.
    deferralCode: 'COMPOSITE_AREA_POINT_PLACEMENT_UNVERIFIED',
    summary: 'a ground area placed at the point the formation resolved',
  },
  // ── P8 orbit (Gate P8) ────────────────────────────────────────────────
  // Five separate claims, five separate ids, opened together: an orbit is
  // executable only when ALL FIVE are open (`ORBIT_CAPABILITIES` below),
  // because an orbit that moves correctly but is priced by a formula that
  // does not describe it is not a shippable skill. Each one's evidence is
  // named on it; `test/composite-orbit-production-gate.test.ts` and
  // `test/composite-orbit-world.test.ts` prove the public doors (loader,
  // appraisal, AuthoritativeWorld) with no capability door at all.
  //
  // An orbit's attacks PERSIST, so the P7 attack-count gate does not vouch
  // for its workload; its per-tick cost is bounded per orb by the ratified
  // precision profile (`C1_BOOTSTRAP_TECHNICAL_PROFILE.orbit`) and measured
  // under sustained load (`scripts/orbit-precision/sustained-load.ts`).
  // See docs/superpowers/specs/2026-09-19-orbit-motion-and-swept-collision.md.
  ORBIT_MOTION: {
    open: true,
    // Deterministic RING phases, signed speed, FOLLOW_CASTER/FIXED_POINT
    // centres, instance write-back that never becomes a collision chord
    // (`composite-orbit-motion`, `composite-orbit-runtime`,
    // `composite-orbit-execution` tests).
    deferralCode: 'COMPOSITE_ORBIT_MOTION_UNVERIFIED',
    summary: 'position driven every tick by the orbit function, with FOLLOW_CASTER and FIXED_POINT centres (P8-1)',
  },
  ORBIT_SWEPT_CONTACT: {
    open: true,
    // Arc-accurate contact under the ratified 0.02 m / 128 sub-step
    // profile. A combination needing more is refused, never approximated:
    // per tick with nothing committed (`advanceOrbits`), and before any
    // cast when the arc alone exceeds it — at the profile's reference tick
    // in the compiler (COMPOSITE_ORBIT_PRECISION_ENVELOPE_EXCEEDED) and at
    // the world's own tick in `beginCast`.
    deferralCode: 'COMPOSITE_ORBIT_CONTACT_PRECISION_UNAPPROVED',
    summary: 'arc-accurate contact within an approved error bound and computation ceiling (P8-2, P8-4)',
  },
  ORBIT_REHIT: {
    open: true,
    // Per-orb, per-target interval, exact at the boundary and independent
    // of subdivision. Reached only through the orbit route in the compiler:
    // V2 `COOLDOWN_PER_TARGET` stays deferred for every other shape
    // (CONTACT_POLICY_RUNTIME_UNIMPLEMENTED).
    deferralCode: 'COMPOSITE_ORBIT_REHIT_UNVERIFIED',
    summary: 'per-instance rehit interval per target, independent between orbs (P8-3)',
  },
  ORBIT_EVALUATION: {
    open: true,
    // Expected contacts from the dwell/encounter model
    // (`orbitEncounterContacts`/`orbitContactExposure`), never the static
    // circle; Burst from a real orbit execution. An encounter whose motion
    // is absent or UNKNOWN still defers (ORBIT_ENCOUNTER_MOTION_UNKNOWN), and
    // a drifting one without the ratified precision defers too.
    deferralCode: 'COMPOSITE_ORBIT_EVALUATION_UNAVAILABLE',
    summary: 'expected hits from a dwell-time model rather than duration/hitInterval (P8-5)',
  },
  ORBIT_LIFECYCLE: {
    open: true,
    // Authored expiry and centre loss dispose the orb and release its root
    // exactly once; a vanished target is never substituted; the source and
    // mastery snapshot is frozen at spawn.
    deferralCode: 'COMPOSITE_ORBIT_LIFECYCLE_UNVERIFIED',
    summary: 'centre loss and authored expiry dispose instances, cancel schedules and release roots (P8-3)',
  },
  DASH_ATTACK: {
    // The runtime (both hosts), the price (`eval/dash-hits.ts`, `eval/chain-hits.ts`) and the simulator agree, and the Lab
    // (`test/monte-carlo/dash-hits-lab.test.ts`) holds the realized hits to the priced ones within its band.
    open: true,
    deferralCode: 'COMPOSITE_DASH_ATTACK_UNVERIFIED',
    summary: 'a stage whose members move the caster to their goal and strike where it lands, a chain\'s included',
  },
  NON_AIMED_DELIVERY_PLACEMENT: {
    open: false,
    // The catch-all: any delivery the placement owner has no mode for. A
    // delivery added later is refused until it is classified, rather than
    // silently taking whichever branch it happens to fall into.
    deferralCode: 'COMPOSITE_DELIVERY_PLACEMENT_UNIMPLEMENTED',
    summary: 'placing a delivery with no defined placement mode',
  },
});

export type CompositeCapabilityResult =
  | { readonly status: 'SUPPORTED'; readonly required: readonly CompositeCapabilityId[] }
  /**
   * A limit the cast provably exceeds (event depth, attack-unit workload, the cast's attack budget): the program is
   * wrong, not unfinished. `DEFERRED` stays for a shape or evidence the engine has not opened.
   */
  | {
      readonly status: 'INVALID';
      readonly code: string;
      readonly path: string;
      readonly message: string;
      readonly required: readonly CompositeCapabilityId[];
    }
  | {
      readonly status: 'DEFERRED';
      readonly code: string;
      readonly message: string;
      /** The unopened capability, when the obstacle is the cast's SHAPE.
       * Absent when the obstacle is its SIZE — see the workload bound. */
      readonly capability?: CompositeCapabilityId;
      readonly required: readonly CompositeCapabilityId[];
    };

/**
 * How many independent attack units one cast may resolve before this gate
 * stops being able to vouch for it.
 *
 * **Not a composite cap, and not a reuse of `maxSpawnedAttacks`.** The
 * argument is narrower than either: a single V2 skill may already spawn up
 * to `limits.maxSpawnedAttacks` attacks in one activation, and
 * `analyzeSafety` enforces that today. A composite cast resolving no more
 * attack units than that therefore produces no more attacks per activation
 * than an already-legal skill — its workload is witnessed by an existing
 * ratified limit rather than by a number invented here.
 *
 * Above it, nothing ratified speaks. `StructuralLimits` has no field for
 * attack-unit fan-out, concurrent instances or area count; the value
 * contract §13.2 names those budgets but no profile carries one. So the
 * honest answer is that the cast is **unjudged**, and an unjudged cast is
 * withheld rather than passed — a large cast getting through is evidence of
 * a missing check, not of safety.
 *
 * Whether `maxSpawnedAttacks` should itself become the composite bound, or
 * whether composite fan-out deserves its own ratified budget, is a
 * technical-profile owner decision this gate deliberately does not make.
 */
export function ratifiedAttackUnitWitness(balanceProfile: BalanceProfile, rarity?: string): number {
  // The cast-limit table gives the tier's reach; a balance profile may narrow it, never widen it.
  return castUnitCap(balanceProfile.limits.maxSpawnedAttacks, rarity);
}

/**
 * Marks a schema variant that is deliberately plain enough to need no
 * capability of its own — the shape a non-composite skill already has.
 * Spelled out rather than left as `undefined` so "no capability" is always
 * a decision someone made, never a gap.
 */
export const NO_CAPABILITY_REQUIRED = 'NO_CAPABILITY_REQUIRED' as const;
type CapabilityFor = CompositeCapabilityId | typeof NO_CAPABILITY_REQUIRED;

/**
 * Every schema variant, classified.
 *
 * These tables are the defence against the failure P5 exposed: a sweep
 * stage and a fraction-anchored release were both executable while no
 * capability described either, so they were open by omission and nothing
 * noticed. `satisfies Record<…>` makes that structurally impossible now —
 * adding a member to any of these unions fails to compile until someone
 * classifies it, and classifying it closed is the safe default.
 */
export const FORMATION_CAPABILITY = Object.freeze({
  SINGLE: NO_CAPABILITY_REQUIRED,
  FAN: 'FAN_VOLLEY',
  PARALLEL: 'PARALLEL_VOLLEY',
  RADIAL: 'RADIAL_VOLLEY',
  RING: 'RING_PLACEMENT',
  SCATTER: 'SCATTER_PLACEMENT',
  CHAIN: 'CHAIN_FORMATION',
} as const satisfies Record<SpatialFormationV1['kind'], CapabilityFor>);

export const STAGE_TIMING_CAPABILITY = Object.freeze({
  AT_RELEASE: NO_CAPABILITY_REQUIRED,
  AFTER_RELEASE: 'DELAYED_STAGE_TIMING',
  AT_STAGE_FRACTION: 'STAGE_FRACTION_TIMING',
  ON_STAGE_EVENT: 'STAGE_EVENT_TIMING',
} as const satisfies Record<Exclude<StageTimingV1['kind'], 'ON_STATUS'>, CapabilityFor>);

/** The timing kinds that belong to a skill's own statuses: a hook is released by what happens to a status. */
export const STATUS_TIMING_CAPABILITY = Object.freeze({
  ON_STATUS: 'SKILL_STATUS',
} as const satisfies Record<Extract<StageTimingV1['kind'], 'ON_STATUS'>, CapabilityFor>);

/** The capability a timing kind requires, whichever table classifies it. */
export function timingCapability(kind: StageTimingV1['kind']): CapabilityFor {
  return kind === 'ON_STATUS' ? STATUS_TIMING_CAPABILITY[kind] : STAGE_TIMING_CAPABILITY[kind];
}

/**
 * Where an event stage stands, classified on its own: `STAGE_TIMING_CAPABILITY` is keyed by the
 * timing kind and so cannot see an anchor that means more than a position.
 */
export const STAGE_EVENT_ANCHOR_CAPABILITY = Object.freeze({
  EVENT_POINT: NO_CAPABILITY_REQUIRED,
  CASTER: NO_CAPABILITY_REQUIRED,
  CAST_AIM: NO_CAPABILITY_REQUIRED,
  MARKED: 'STAGE_MARK_DETONATION',
} as const satisfies Record<Exclude<StageEventAnchorV1, 'CARRIERS'>, CapabilityFor>);

/** The anchor of a sweep over a skill status's carriers (a sweep over marks is `MARKED`, above). */
export const STATUS_EVENT_ANCHOR_CAPABILITY = Object.freeze({
  CARRIERS: 'SKILL_STATUS',
} as const satisfies Record<Extract<StageEventAnchorV1, 'CARRIERS'>, CapabilityFor>);

/** The capability an event anchor requires, whichever table classifies it. */
export function anchorCapability(anchor: StageEventAnchorV1): CapabilityFor {
  return anchor === 'CARRIERS' ? STATUS_EVENT_ANCHOR_CAPABILITY[anchor] : STAGE_EVENT_ANCHOR_CAPABILITY[anchor];
}

export const STAGE_MOTION_CAPABILITY = Object.freeze({
  INSTANT: NO_CAPABILITY_REQUIRED,
  THRUST: NO_CAPABILITY_REQUIRED,
  PROJECTILE: 'PROJECTILE_STAGE',
  WEAPON_SWEEP: 'WEAPON_SWEEP_STAGE',
} as const satisfies Record<CanonicalMotionV2['kind'], CapabilityFor>);

/**
 * The V3 path-motion axis, classified exhaustively in its own right.
 *
 * `STAGE_MOTION_CAPABILITY` cannot cover this: it is keyed by
 * `CanonicalMotionV2['kind']`, so a V3-only motion would never appear there
 * and would be required by nothing — open by omission, the P5 lesson. A new
 * `StageMotionV1` variant fails to compile until it is classified here.
 */
export const STAGE_PATH_MOTION_CAPABILITY = Object.freeze({
  ORBIT: 'ORBIT_MOTION',
  DASH: 'DASH_ATTACK',
} as const satisfies Record<StageMotionV1['kind'], CapabilityFor>);

export const MEMBER_SCHEDULE_CAPABILITY = Object.freeze({
  SIMULTANEOUS: NO_CAPABILITY_REQUIRED,
  SEQUENTIAL: 'SEQUENTIAL_MEMBERS',
  STAGGERED: 'STAGGERED_MEMBERS',
} as const satisfies Record<MemberScheduleV1['kind'], CapabilityFor>);

export const VOLLEY_CAPABILITY = Object.freeze({
  ONCE: NO_CAPABILITY_REQUIRED,
  REPEAT: 'REPEATED_VOLLEY',
} as const satisfies Record<VolleyScheduleV1['kind'], CapabilityFor>);

function addIfRequired(required: Set<CompositeCapabilityId>, capability: CapabilityFor): void {
  if (capability !== NO_CAPABILITY_REQUIRED) required.add(capability);
}

/**
 * The capabilities one plan's shape requires.
 *
 * Exported because it is useful on its own — a caller can ask what a skill
 * needs before asking whether it may have it, and the completion record for
 * a capability can name exactly which plans exercise it.
 */
export function requiredCompositeCapabilities(plan: CompiledSkillPlanV3): readonly CompositeCapabilityId[] {
  const elementRequired = closedElementStatusNames(plan).length > 0;
  if (isDegenerateCompositeSkill(plan.canonicalSkill)) return elementRequired ? ['ELEMENT_STATUS', 'SINGLE_V3_COMPAT'] : ['SINGLE_V3_COMPAT'];

  const required = new Set<CompositeCapabilityId>();
  if (elementRequired) required.add('ELEMENT_STATUS');
  if ((plan.canonicalSkill.statuses?.length ?? 0) > 0) required.add('SKILL_STATUS');
  if (plan.canonicalSkill.stages.length > 1) required.add('PARALLEL_STAGES');

  for (const authored of plan.canonicalSkill.stages) {
    // Every axis is classified by a table, so a schema variant cannot slip
    // through unclassified the way a weapon sweep once did.
    addIfRequired(required, FORMATION_CAPABILITY[authored.pattern.formation.kind]);
    addIfRequired(required, VOLLEY_CAPABILITY[authored.pattern.volleys.kind]);
    addIfRequired(required, MEMBER_SCHEDULE_CAPABILITY[authored.pattern.memberSchedule.kind]);
    addIfRequired(required, STAGE_MOTION_CAPABILITY[authored.application.motion.kind]);
    addIfRequired(required, timingCapability(authored.timing.kind));
    if (authored.timing.kind === 'ON_STAGE_EVENT') addIfRequired(required, anchorCapability(authored.timing.anchor));
    if (authored.timing.kind === 'ON_STAGE_EVENT' && uncountableStageEvent(authored.timing) !== undefined) required.add('STAGE_EVENT_SCOPED_EVERY_N');

    if (authored.motion !== undefined) {
      addIfRequired(required, STAGE_PATH_MOTION_CAPABILITY[authored.motion.kind]);
      if (authored.motion.kind === 'ORBIT') {
        // An orbit is not one capability. Contact along the arc, the rehit
        // map, the dwell-time valuation and the disposal rules are each
        // separately evidenced, so an orbit requires all five and opening
        // any four still refuses it.
        required.add('ORBIT_SWEPT_CONTACT');
        required.add('ORBIT_REHIT');
        required.add('ORBIT_EVALUATION');
        required.add('ORBIT_LIFECYCLE');
      }
    }
  }

  for (const stage of plan.stages) {
    if (stage.units.some((unit) => unit.transforms.length > 1)) {
      required.add(
        stage.runtimePlan.canonicalSkill.application.geometry.owner === 'WEAPON_CONTACT'
          ? 'WEAPON_FOOTPRINT_UNION'
          : 'MULTI_FOOTPRINT_UNION',
      );
    }
    // An event stage is always placed: it is anchored where its event
    // happened, not where the cast was aimed.
    // A chain's members after the first are placed at the enemies it strikes.
    const places = stage.timing.kind === 'ON_STAGE_EVENT' || stage.timing.kind === 'ON_STATUS' || stage.chain !== undefined
      || stage.units.some((unit) => unit.transforms.some((t) => !isIdentityTransform(t)));
    if (!places) continue;
    const lowered = resolveLegacyDeliveryForApplication(stage.runtimePlan.canonicalSkill.application);
    const mode = lowered.ok ? deliveryPlacementMode(lowered.delivery.type) : undefined;
    if (mode === undefined) required.add('NON_AIMED_DELIVERY_PLACEMENT');
    else if (mode === 'POINT') required.add('AREA_POINT_PLACEMENT');
  }

  return [...required].sort();
}

/** The element packages whose tests have passed: an element status naming one needs no more than the rest of its cast does. Each package spec adds its own. */
export const ELEMENT_STATUS_OPEN_NAMES: readonly string[] = Object.freeze(['frostbite', 'freeze', 'conductive', 'shock']);

/** The names of the element statuses an effect list applies, nested effects included. */
function elementStatusNames(effects: readonly unknown[]): string[] {
  return effects.flatMap((effect) => {
    const node = effect as { readonly kind?: string; readonly name?: string; readonly children?: readonly unknown[]; readonly effects?: readonly unknown[] };
    if (node.kind === 'element_status') return [String(node.name)];
    return elementStatusNames([...(node.children ?? []), ...(node.effects ?? [])]);
  });
}

function closedElementStatusNames(plan: CompiledSkillPlanV3): string[] {
  return plan.canonicalSkill.stages.flatMap((stage) => elementStatusNames(stage.effects)).filter((name) => !ELEMENT_STATUS_OPEN_NAMES.includes(name));
}

/** The stage a closed capability came from, for a message that names it. */
function stageRequiring(plan: CompiledSkillPlanV3, capability: CompositeCapabilityId): CompiledStageV3 | undefined {
  return plan.stages.find((stage) => {
    const authored = plan.canonicalSkill.stages.find((s) => s.stageId === stage.stageId);
    if (authored !== undefined && FORMATION_CAPABILITY[authored.pattern.formation.kind] === capability) return true;
    if (authored !== undefined && MEMBER_SCHEDULE_CAPABILITY[authored.pattern.memberSchedule.kind] === capability) return true;
    if (capability === 'MULTI_FOOTPRINT_UNION' || capability === 'WEAPON_FOOTPRINT_UNION') {
      return stage.units.some((unit) => unit.transforms.length > 1);
    }
    if (capability === 'WEAPON_SWEEP_STAGE') return authored?.application.motion.kind === 'WEAPON_SWEEP';
    if (capability === 'DASH_ATTACK') return authored?.motion?.kind === 'DASH';
    if (capability === 'STAGE_FRACTION_TIMING') return authored?.timing.kind === 'AT_STAGE_FRACTION';
    if (capability === 'STAGE_EVENT_TIMING') return authored?.timing.kind === 'ON_STAGE_EVENT';
    if (capability === 'STAGE_MARK_DETONATION') return authored?.timing.kind === 'ON_STAGE_EVENT' && authored.timing.anchor === 'MARKED';
    if (capability === 'ELEMENT_STATUS') return authored !== undefined && elementStatusNames(authored.effects).some((name) => !ELEMENT_STATUS_OPEN_NAMES.includes(name));
    if (capability === 'SKILL_STATUS') return authored?.timing.kind === 'ON_STATUS' || (authored?.timing.kind === 'ON_STAGE_EVENT' && authored.timing.anchor === 'CARRIERS');
    if (capability === 'STAGE_EVENT_SCOPED_EVERY_N') return authored?.timing.kind === 'ON_STAGE_EVENT' && uncountableStageEvent(authored.timing) !== undefined;
    return false;
  });
}

/**
 * Assess whether a compiled plan is a verified composite combination.
 *
 * Reported in a stable capability order so a plan needing two closed
 * capabilities always names the same one — an unstable answer here would
 * make the four boundaries appear to disagree.
 */
export function assessCompositeCapability(
  plan: CompiledSkillPlanV3,
  balanceProfile: BalanceProfile = SVP_0_1,
  grant?: OrbitMeasurementGrant,
  /** The rarity tier the skill is judged under; absent means `DEFAULT_CAST_REACH_RARITY`'s row. */
  rarity?: string,
  /** The attack limit the executing host imposes (`RuntimeCtx.executionLimits.maxSpawnedAttacks`); it can only narrow the witness. */
  executionAttackLimit?: number,
): CompositeCapabilityResult {
  return assessInContext(plan, unlimitedHostContext(balanceProfile, rarity, executionAttackLimit), grant);
}

/** How many verdicts were asked for (`requests`) and how many of them were worked out rather than remembered (`assessments`); the tests that count assessments read it. */
export const capabilityVerdictStats = { requests: 0, assessments: 0 };

/** The verdict on one plan in a stated legality context: its profile, rarity and every limit its host enforces. */
export function assessInContext(plan: CompiledSkillPlanV3, context: LegalityContext, grant?: OrbitMeasurementGrant): CompositeCapabilityResult {
  capabilityVerdictStats.requests += 1;
  // A verdict depends on the plan and its legality context alone; a call that sees capabilities opened
  // by a grant or a test scope neither reads nor writes what is remembered.
  if (grant !== undefined || capabilityOverridesActive()) return assessUncached(plan, context, grant);
  let byProfile = verdicts.get(plan);
  if (byProfile === undefined) { byProfile = new WeakMap(); verdicts.set(plan, byProfile); }
  let byContext = byProfile.get(context.profile);
  if (byContext === undefined) { byContext = new Map(); byProfile.set(context.profile, byContext); }
  const key = legalityContextKey(context);
  let verdict = byContext.get(key);
  if (verdict === undefined) { verdict = assessUncached(plan, context, grant); byContext.set(key, verdict); }
  return verdict;
}

/** A verdict given when a plan was owned, with the plan and the legality context it was given for. */
export interface ContextBoundVerdict {
  readonly plan: CompiledSkillPlanV3;
  readonly context: LegalityContext;
  readonly verdict: CompositeCapabilityResult;
}

/**
 * The verdict on `plan` in `context`. A verdict bound at ownership answers only for the same plan in an
 * equal context (same profile object, same rarity and host limits) and only while no grant or test scope
 * has opened capabilities; any other call is judged in its own context.
 */
export function verdictInContext(
  plan: CompiledSkillPlanV3,
  bound: ContextBoundVerdict | undefined,
  context: LegalityContext,
  grant?: OrbitMeasurementGrant,
): CompositeCapabilityResult {
  if (bound !== undefined && grant === undefined && !capabilityOverridesActive() && bound.plan === plan
    && bound.context.profile === context.profile && legalityContextKey(bound.context) === legalityContextKey(context)) return bound.verdict;
  return assessInContext(plan, context, grant);
}

/** Verdicts already given, per compiled plan (by identity), balance profile (by identity) and legality context. */
const verdicts = new WeakMap<CompiledSkillPlanV3, WeakMap<BalanceProfile, Map<string, CompositeCapabilityResult>>>();

function assessUncached(plan: CompiledSkillPlanV3, context: LegalityContext, grant?: OrbitMeasurementGrant): CompositeCapabilityResult {
  const { profile: balanceProfile, rarity } = context;
  const executionAttackLimit = context.hostLimits.maxSpawnedAttacks;
  capabilityVerdictStats.assessments += 1;
  const required = requiredCompositeCapabilities(plan);

  for (const capability of required) {
    const entry = COMPOSITE_CAPABILITIES[capability];
    if (capabilityIsOpen(capability, grant)) continue;
    const stage = stageRequiring(plan, capability);
    return {
      status: 'DEFERRED',
      code: entry.deferralCode,
      capability,
      required,
      message: stage === undefined
        ? `this cast requires ${capability} — ${entry.summary} — which is not open yet`
        : `stage ${stage.stageId} requires ${capability} — ${entry.summary} — which is not open yet`,
    };
  }

  // Event generations count with the effect graph's follow-up generations toward the
  // tier's limit, so a chain that already uses it up leaves a follow-up nowhere to go.
  const depthBreach = eventDepthBreach(plan.canonicalSkill, rarity, context.hostLimits.maxProcGeneration);
  if (depthBreach !== undefined) return { status: 'INVALID', ...depthBreach, required };

  // Shape is fine; now whether the world can compute its release schedule.
  // A stage anchored to a fraction of a reference with no measurable duration
  // has no release tick, and the world only learns that after the cast has
  // been paid for. Asking the scheduler itself here keeps the loader, the
  // evaluator and the pre-payment check on the schedule the world will run.
  // Whether a reference has a duration does not depend on the tick rate, so
  // the probe rate is arbitrary.
  const schedule = scheduleCompositeCast(plan, 0, 1);
  if (!schedule.ok) return { status: 'DEFERRED', code: schedule.code, required, message: schedule.message };

  const witness = Math.min(ratifiedAttackUnitWitness(balanceProfile, rarity), executionAttackLimit);
  // Chain members, events, hooks and sweeps draw on the same budget, and a cast the budget cuts short is priced whole: the
  // cast's own limit (its rarity's row, never above the profile's, nor the host's) is the one it must fit, not the default row's.
  const truncated = castBudgetBreach(plan.canonicalSkill, witness);
  if (truncated !== undefined) return { status: 'INVALID', ...truncated, required };

  // Shape is fine; now size. A cast whose workload no ratified limit can
  // speak to is withheld, because "nothing rejected it" is not a safety
  // result — it is an unmeasured dimension.
  // An event stage counts once here: it must fit beside the plain stages.
  // How often it comes out is limited at run time to what the witness leaves
  // (`executeCompositeCast`), so the cast never exceeds it as a whole.
  const attackUnits = plan.stages.reduce((sum, stage) => sum + stage.units.length, 0);
  if (attackUnits > witness) {
    return {
      status: 'INVALID',
      code: 'COMPOSITE_WORKLOAD_PROFILE_UNAVAILABLE',
      path: 'stages',
      required,
      message:
        `this cast resolves ${attackUnits} independent attack units. No ratified limit bounds composite ` +
        `attack-unit fan-out, concurrent instances or area count, and the largest per-activation attack ` +
        `count any existing limit admits is ${witness} (limits.maxSpawnedAttacks, which today governs ` +
        'effect-AST secondary attacks). Beyond that this gate cannot vouch for the cast, so it is ' +
        'withheld pending a ratified workload budget rather than passed unmeasured',
    };
  }

  return { status: 'SUPPORTED', required };
}

/**
 * The five claims an orbit needs, all of them.
 *
 * Executability is the conjunction: motion without verified contact is an
 * orb that passes through enemies, contact without a dwell-time model is a
 * skill nobody can price, and either without lifecycle cleanup leaks. A
 * partially-evidenced orbit opens nothing.
 */
export const ORBIT_CAPABILITIES: readonly CompositeCapabilityId[] = Object.freeze([
  'ORBIT_MOTION',
  'ORBIT_SWEPT_CONTACT',
  'ORBIT_REHIT',
  'ORBIT_EVALUATION',
  'ORBIT_LIFECYCLE',
]);

/** True once every orbit claim has its own evidence. */
/**
 * Capabilities treated as open for the duration of one call. **Tests only.**
 *
 * The registry's `open` flags are the release decision and are not touched
 * by this: nothing here can leave a capability open after the call
 * returns, and production has no way to enter it. It exists so an
 * execution path can be exercised — and so refused when its evidence is
 * missing — without the two being the same switch. Flipping a flag to
 * write a test is how a shape ships unverified.
 */
const scopedOpen = new Set<CompositeCapabilityId>();

/** True while a test scope holds capabilities open: nothing derived from the registry may be remembered then. */
export function capabilityOverridesActive(): boolean {
  return scopedOpen.size > 0;
}

export function TEST_ONLY_withCapabilitiesOpen<T>(ids: readonly CompositeCapabilityId[], run: () => T): T {
  const added = ids.filter((id) => !scopedOpen.has(id));
  for (const id of added) scopedOpen.add(id);
  try {
    return run();
  } finally {
    for (const id of added) scopedOpen.delete(id);
  }
}

/**
 * A one-call, explicitly-passed grant that `capabilityIsOpen` accepts as
 * proof the caller is `src/burst/*.ts`'s orbit measurement path — the
 * MEASUREMENT-ONLY counterpart to `TEST_ONLY_withCapabilitiesOpen` (P8-5).
 *
 * P8-5's independent review found that an earlier version of this door used
 * a module-level `Set` the way `TEST_ONLY_withCapabilitiesOpen` does:
 * `capabilityIsOpen` read the Set's membership with no notion of WHICH call
 * on the stack was asking, so anything invoked synchronously nested inside
 * one measurement call — including a caller-supplied telemetry sink hook
 * that reenters `compileCompositeSkill` for a wholly unrelated skill — saw
 * every orbit capability open too. Verified directly: a `compileCompositeSkill`
 * call made from inside a sink callback nested in an active
 * `runCompositeBenchmarkScenario` run returned `COMPILED` for a plan that,
 * called the same way outside any measurement, is `DEFERRED`.
 *
 * The fix is this token, not a flag. Nothing module-level marks capabilities
 * open; the only way a call sees them open is to receive this exact object,
 * explicitly, as an argument — threaded from `withOrbitMeasurementCapabilitiesOpen`
 * down through `ownCompositeSkill`/`loadCanonicalSkill`/`compileCompositeSkill`/
 * `executeCompositeCast`/`assessCompositeCapability` to `capabilityIsOpen`. A
 * reentrant call nested on the very same synchronous stack but NOT handed
 * this token — because its caller is an unrelated hook, not the measurement
 * module itself — receives `undefined` and sees every orbit capability
 * closed, exactly like any other caller. There is no ambient state left to
 * leak.
 *
 * What this does NOT do: it never touches `COMPOSITE_CAPABILITIES[id].open`
 * — the registry's own answer for the general loader, world casts and
 * player-facing appraisal — and no caller that omits this parameter can
 * ever receive it by accident.
 *
 * **Redundant since Gate P8.** The grant only ever covers
 * `ORBIT_CAPABILITIES`, which the registry now opens itself, so a granted
 * call and an ungranted one get the same answer. It is kept only because it
 * is threaded as a parameter through the loader, compiler, composite
 * executor, orbit harness and Burst measurement; removing it is a separate
 * API cleanup. Only `src/burst/*.ts`'s orbit-aware measurement adapter may
 * construct and pass it.
 */
export interface OrbitMeasurementGrant {
  readonly kind: 'ORBIT_MEASUREMENT_GRANT';
}

const ORBIT_MEASUREMENT_GRANT: OrbitMeasurementGrant = Object.freeze({ kind: 'ORBIT_MEASUREMENT_GRANT' });

export function withOrbitMeasurementCapabilitiesOpen<T>(run: (grant: OrbitMeasurementGrant) => T): T {
  return run(ORBIT_MEASUREMENT_GRANT);
}

/** The single reading of "is this capability open right now". `grant` is
 * `OrbitMeasurementGrant` or `undefined` — never inferred from ambient
 * state, so a caller must be explicitly handed one to see an orbit
 * capability open through this door. */
export function capabilityIsOpen(id: CompositeCapabilityId, grant?: OrbitMeasurementGrant): boolean {
  return COMPOSITE_CAPABILITIES[id].open || scopedOpen.has(id) || (grant !== undefined && ORBIT_CAPABILITIES.includes(id));
}

export function orbitIsExecutable(grant?: OrbitMeasurementGrant): boolean {
  return ORBIT_CAPABILITIES.every((id) => capabilityIsOpen(id, grant));
}

/** Convenience predicate for call sites that only branch on the outcome. */
export function compositeCapabilityIsOpen(plan: CompiledSkillPlanV3): boolean {
  return assessCompositeCapability(plan).status === 'SUPPORTED';
}

/** The refusals of a cast-wide limit: a proven overrun (INVALID), judged only once the cast's shape is valid and every capability it needs is open. */
export const CAST_LIMIT_CODES: readonly string[] = Object.freeze(['COMPOSITE_STAGE_EVENT_DEPTH_EXCEEDED', CAST_BUDGET_TRUNCATION, 'COMPOSITE_WORKLOAD_PROFILE_UNAVAILABLE']);

/** The generations a chain of events may nest: the tier's, and no more than the host's own limit (absent: the tier's alone). */
export function eventGenerationLimit(rarity?: string, hostMaxGeneration: number = Infinity): number {
  return Math.min(castReachLimits(rarity).maxGenerations, hostMaxGeneration);
}

/** Whether a chain of `depth` generations is deeper than the limit (`eventGenerationLimit`). The one decision every layer maps to its own code. */
export function eventDepthExceeded(depth: number, rarity?: string, hostMaxGeneration?: number): boolean {
  return depth > eventGenerationLimit(rarity, hostMaxGeneration);
}

/** An issue a limit check gives: the stable code, the path of the definition it is about, and the reason. */
export interface LimitBreach { readonly code: string; readonly path: string; readonly message: string }

/** The first stage nested deeper than the limit allows; `undefined` when every stage fits. */
export function eventDepthBreach(skill: CanonicalSkillV3Input, rarity?: string, hostMaxGeneration?: number): LimitBreach | undefined {
  const limit = eventGenerationLimit(rarity, hostMaxGeneration);
  const stages = skill.stages as unknown as readonly Record<string, unknown>[];
  const depths = statusStageDepths(canonicalStatusModel(stages, skill.statuses).stages, limit);
  for (const [index, stage] of stages.entries()) {
    const depth = depths.get(stage.stageId as string) ?? 0;
    if (depth > limit) return { code: 'COMPOSITE_STAGE_EVENT_DEPTH_EXCEEDED', path: `stages[${index}].timing`, message: `stage ${String(stage.stageId)} nests ${depth} events deep; the limit is ${limit}` };
  }
  return undefined;
}

/** The cast's worst-case follow-up releases against the one attack budget (`limit`) the runtime spends them from; `undefined` when they fit. */
export function castBudgetBreach(skill: CanonicalSkillV3Input, limit: number): LimitBreach | undefined {
  const message = castBudgetTruncation(skill, limit);
  return message === undefined ? undefined : { code: CAST_BUDGET_TRUNCATION, path: 'stages', message };
}

/**
 * The depth and budget limits of a cast that lowers to a single application. Such a cast has no plan for
 * `assessCompositeCapability` to judge, but the limits bind it as they bind any cast.
 */
export function castLimitBreach(skill: CanonicalSkillV3Input, context: LegalityContext): LimitBreach | undefined {
  return eventDepthBreach(skill, context.rarity, context.hostLimits.maxProcGeneration) ?? castBudgetBreach(skill, Math.min(castUnitCap(context.profile.limits.maxSpawnedAttacks, context.rarity), context.hostLimits.maxSpawnedAttacks));
}
